/**
 * Meta Ads Sync — page de configuration + historique synchronisations.
 *
 * Flux : connexion Google OAuth existante (/settings-google) + feuille
 * Google Sheets alimentée par les formulaires instantanés Meta Ads.
 * Les prospects importés deviennent des dossiers "MaPrimeAdapt'" dans
 * la rubrique Dossiers CEE (aide_type=maprimeadapt).
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { extractErrorMessage } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet,
  Link as LinkIcon, Mail, Clock,
} from 'lucide-react';

const INTERVAL_OPTIONS = [
  { value: 300, label: '5 minutes (recommandé)' },
  { value: 900, label: '15 minutes' },
  { value: 3600, label: '1 heure' },
  { value: 21600, label: '6 heures' },
  { value: 86400, label: '24 heures' },
];

export default function MetaSyncSettings() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [liveMode, setLiveMode] = useState(null); // {enabled, updated_at, updated_by}
  const [togglingLive, setTogglingLive] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillReport, setBackfillReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, lm] = await Promise.all([
        api.get('/meta-sync/config'),
        api.get('/meta-sync/runs?limit=10'),
        api.get('/prospect-quiz/live-mode/status'),
      ]);
      setCfg(c.data);
      setRuns(r.data?.runs || []);
      setLiveMode(lm.data);
    } catch (e) {
      toast.error(extractErrorMessage(e) || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const r = await api.put('/meta-sync/config', {
        spreadsheet_id: cfg.spreadsheet_id || null,
        tab_name: cfg.tab_name || 'Feuille 1',
        enabled: !!cfg.enabled,
        test_email: cfg.test_email || null,
        go_live_emails: !!cfg.go_live_emails,
        sync_interval_seconds: parseInt(cfg.sync_interval_seconds) || 300,
        excluded_lead_ids: cfg.excluded_lead_ids || [],
      });
      setCfg(r.data);
      toast.success('Configuration enregistrée');
    } catch (e) {
      toast.error(extractErrorMessage(e) || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/meta-sync/sync-now');
      const s = r.data;
      if (s.errors && s.errors.length) {
        toast.warning(`Sync terminé avec erreurs : ${s.errors[0]}`);
      } else {
        toast.success(
          `Sync OK · ${s.ok_count} créé(s) · ${s.dup_count} doublon(s) · ${s.excluded_count} exclu(s)`,
        );
      }
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e) || 'Erreur synchronisation');
    } finally { setSyncing(false); }
  };

  const toggleLiveMode = async (checked) => {
    setTogglingLive(true);
    try {
      const r = await api.put('/prospect-quiz/live-mode/status',
                                { enabled: !!checked });
      setLiveMode((prev) => ({ ...(prev || {}), enabled: r.data.enabled }));
      toast.success(r.data.enabled
        ? 'Envois réels ACTIVÉS'
        : 'Envois réels désactivés — mode TEST (aberbry@gmail.com)');
    } catch (e) {
      toast.error(extractErrorMessage(e) || 'Erreur basculement live mode');
    } finally { setTogglingLive(false); }
  };

  const runBackfill = async () => {
    if (!window.confirm(
      'Lancer l\'envoi aux prospects MaPrimeAdapt\' existants ?\n\n'
      + '• Les dossiers sans e-mail valide seront marqués « À contacter par téléphone ».\n'
      + '• Les dossiers déjà envoyés en MODE RÉEL ou ayant déjà répondu ne seront pas touchés.\n'
      + '• Les anciens envois TEST (redirigés vers aberbry@gmail.com) seront réinitialisés et un nouveau lien 7 jours sera envoyé au demandeur.',
    )) return;
    setBackfilling(true);
    setBackfillReport(null);
    try {
      const r = await api.post('/prospect-quiz/send-pending-batch');
      setBackfillReport(r.data);
      const t = r.data?.totals || {};
      toast.success(
        `Bilan : ${t.accepted || 0} acceptés · ${t.phone_needed || 0} tél. requis · `
        + `${t.failed || 0} échecs · ${t.excluded || 0} exclus`,
      );
    } catch (e) {
      toast.error(extractErrorMessage(e) || 'Erreur backfill');
    } finally { setBackfilling(false); }
  };

  if (loading || !cfg) {
    return <div className="p-6 text-slate-500">Chargement…</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="meta-sync-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Import prospects Meta Ads
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Les prospects des campagnes Meta (formulaires instantanés) alimentent
          une feuille Google Sheets. Reno Resolve les importe automatiquement
          dans Dossiers CEE comme <strong>MaPrimeAdapt'</strong>.
        </p>
      </div>

      {/* Pré-requis Google */}
      <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3"
            data-testid="meta-sync-google-prereq">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 flex-1">
          <div className="font-semibold">Pré-requis — connexion Google</div>
          <div className="mt-1">
            Vous devez connecter un compte Google <strong>ayant accès à la
            feuille Prospect MaprimeAdapt'</strong> depuis{' '}
            <Link to="/settings/google" className="underline font-medium">
              Paramètres → Google
            </Link>.
            Le scope <code>spreadsheets</code> est déjà demandé automatiquement.
          </div>
        </div>
      </Card>

      {/* Config */}
      <Card className="p-5 space-y-4 border-slate-200"
            data-testid="meta-sync-config-card">
        <div className="font-semibold text-slate-900 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4" /> Feuille Google Sheets
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600 space-y-1">
            <div>ID de la feuille (spreadsheet_id)</div>
            <Input
              value={cfg.spreadsheet_id || ''}
              onChange={(e) => setCfg({ ...cfg, spreadsheet_id: e.target.value })}
              placeholder="1A93Xa_YDE1ADbvlGAJTEVsHTL4oenOPqn3RSzVv5Bns"
              data-testid="meta-sync-spreadsheet-input"
            />
          </label>
          <label className="text-xs text-slate-600 space-y-1">
            <div>Onglet</div>
            <Input
              value={cfg.tab_name || ''}
              onChange={(e) => setCfg({ ...cfg, tab_name: e.target.value })}
              placeholder="Feuille 1"
              data-testid="meta-sync-tab-input"
            />
          </label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            data-testid="meta-sync-enabled-switch"
          />
          <div className="text-sm">
            <div className="font-medium text-slate-900">
              Synchronisation automatique
            </div>
            <div className="text-xs text-slate-500">
              Récupération périodique en arrière-plan côté serveur
            </div>
          </div>
        </div>
        <label className="text-xs text-slate-600 space-y-1 block max-w-sm">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Fréquence
          </div>
          <select
            value={cfg.sync_interval_seconds || 300}
            onChange={(e) => setCfg({
              ...cfg, sync_interval_seconds: parseInt(e.target.value),
            })}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
            data-testid="meta-sync-interval-select"
          >
            {INTERVAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </Card>

      {/* Email mode test */}
      <Card className="p-5 space-y-4 border-slate-200"
            data-testid="meta-sync-email-card">
        <div className="font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-4 h-4" /> E-mails de préqualification (mode test)
        </div>
        <label className="text-xs text-slate-600 space-y-1 block">
          <div>Adresse de redirection des e-mails de test</div>
          <Input
            type="email"
            value={cfg.test_email || ''}
            onChange={(e) => setCfg({ ...cfg, test_email: e.target.value })}
            placeholder="votre.email@renoresolve.fr"
            data-testid="meta-sync-test-email-input"
          />
          <div className="text-[11px] text-slate-500">
            Tant que « Envoi réel » est désactivé, TOUS les e-mails de
            préqualification (quand ils seront activés en Phase 2) sont
            redirigés vers cette adresse. Aucun prospect réel ne reçoit
            d'e-mail.
          </div>
        </label>
        <div className="flex items-start gap-3 pt-2 border-t border-slate-200 mt-2"
             data-testid="meta-sync-livemode-row">
          <Switch
            checked={!!(liveMode && liveMode.enabled)}
            onCheckedChange={toggleLiveMode}
            disabled={togglingLive || !liveMode}
            data-testid="meta-sync-livemode-switch"
          />
          <div className="text-sm flex-1">
            <div className="font-medium text-slate-900 flex items-center gap-2">
              Envois réels aux prospects MaPrimeAdapt'
              {liveMode?.enabled ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                  ACTIF
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                  TEST — redirigé vers aberbry@gmail.com
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Quand ACTIF : l'e-mail de préqualification part vers l'adresse
              du <b>demandeur Meta</b> (jamais présumer que c'est celle du
              bénéficiaire). Lien personnel valide 7 jours. Pas de relance
              automatique.
            </div>
            {liveMode?.updated_at && (
              <div className="text-[10px] text-slate-400 mt-1">
                Dernière modif. : {new Date(liveMode.updated_at).toLocaleString('fr-FR')}
                {liveMode.updated_by && <> par {liveMode.updated_by}</>}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Backfill — envoi aux prospects déjà importés */}
      <Card className="p-5 border-slate-200" data-testid="meta-sync-backfill-card">
        <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Envoyer le quiz aux prospects déjà importés
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Lance l'envoi aux dossiers MaPrimeAdapt' éligibles. Respecte les
          exclusions : démo, corbeille, dupliqués manuels, statuts avancés
          (offre engagée, travaux, payé…), déjà envoyé en RÉEL ou déjà répondu.
          Les prospects sans e-mail valide sont marqués « À contacter par
          téléphone ».
        </p>
        <div className="flex gap-2">
          <Button
            onClick={runBackfill}
            disabled={backfilling}
            className="bg-[#004AAD] hover:bg-[#003a85] text-white"
            data-testid="meta-sync-backfill-btn">
            {backfilling ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1" />Envoi en cours…</>
            ) : (
              <><Mail className="w-4 h-4 mr-1" />Lancer les envois</>
            )}
          </Button>
        </div>
        {backfillReport && (
          <div className="mt-4 text-xs space-y-2" data-testid="meta-sync-backfill-report">
            <div className="font-semibold text-slate-900">Bilan</div>
            <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <li className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1">
                <div className="text-emerald-700 font-bold text-lg">
                  {backfillReport.totals?.accepted || 0}
                </div>
                <div className="text-emerald-700">Mails acceptés</div>
              </li>
              <li className="rounded-md bg-blue-50 border border-blue-200 px-2 py-1">
                <div className="text-blue-700 font-bold text-lg">
                  {backfillReport.totals?.phone_needed || 0}
                </div>
                <div className="text-blue-700">À contacter par tél.</div>
              </li>
              <li className="rounded-md bg-red-50 border border-red-200 px-2 py-1">
                <div className="text-red-700 font-bold text-lg">
                  {backfillReport.totals?.failed || 0}
                </div>
                <div className="text-red-700">Erreurs SMTP</div>
              </li>
              <li className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                <div className="text-slate-700 font-bold text-lg">
                  {backfillReport.totals?.excluded || 0}
                </div>
                <div className="text-slate-700">Dossiers exclus</div>
              </li>
            </ul>
            {!!backfillReport.reset_test_sent?.length && (
              <details className="rounded-md border border-amber-200 bg-amber-50 p-2">
                <summary className="cursor-pointer font-semibold text-amber-700">
                  {backfillReport.reset_test_sent.length} ancien(s) envoi(s) TEST réinitialisé(s)
                </summary>
                <ul className="mt-2 space-y-1">
                  {backfillReport.reset_test_sent.map((r) => (
                    <li key={r.dossier_id} className="font-mono text-[11px]">
                      {r.dossier_id} — {r.beneficiaire_nom} ({r.demandeur_email || 'sans email demandeur'})
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {!!backfillReport.accepted?.length && (
              <details className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
                <summary className="cursor-pointer font-semibold text-emerald-700">
                  Détail des mails acceptés
                </summary>
                <ul className="mt-2 space-y-1">
                  {backfillReport.accepted.map((e, i) => (
                    <li key={i} className="font-mono text-[11px]">
                      {e.dossier_id} → {e.recipient} ({e.mode})
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {!!backfillReport.phone_needed?.length && (
              <details className="rounded-md border border-blue-200 bg-blue-50 p-2">
                <summary className="cursor-pointer font-semibold text-blue-700">
                  Dossiers à contacter par téléphone
                </summary>
                <ul className="mt-2 space-y-1">
                  {backfillReport.phone_needed.map((e, i) => (
                    <li key={i} className="font-mono text-[11px]">{e.dossier_id}</li>
                  ))}
                </ul>
              </details>
            )}
            {!!backfillReport.failed?.length && (
              <details open className="rounded-md border border-red-200 bg-red-50 p-2">
                <summary className="cursor-pointer font-semibold text-red-700">
                  Erreurs SMTP
                </summary>
                <ul className="mt-2 space-y-1">
                  {backfillReport.failed.map((e, i) => (
                    <li key={i} className="font-mono text-[11px]">
                      {e.dossier_id} · {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {!!backfillReport.excluded?.length && (
              <details className="rounded-md border border-slate-200 bg-slate-50 p-2">
                <summary className="cursor-pointer font-semibold text-slate-700">
                  Dossiers exclus par motif
                </summary>
                <ul className="mt-2 space-y-1">
                  {backfillReport.excluded.map((e, i) => (
                    <li key={i} className="font-mono text-[11px]">
                      {e.dossier_id} — {e.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving}
                data-testid="meta-sync-save-btn"
                className="bg-[#1C49A6] hover:bg-[#163a85] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
        </Button>
        <Button onClick={syncNow} disabled={syncing || !cfg.spreadsheet_id}
                variant="outline"
                data-testid="meta-sync-sync-now-btn">
          {syncing ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-1" />Sync…</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-1" />Synchroniser maintenant</>
          )}
        </Button>
      </div>

      {/* Historique */}
      <Card className="p-5 border-slate-200" data-testid="meta-sync-runs-card">
        <div className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <LinkIcon className="w-4 h-4" /> Historique des synchronisations
        </div>
        {runs.length === 0 ? (
          <div className="text-sm text-slate-500">
            Aucune synchronisation effectuée pour l'instant.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Créés</th>
                  <th className="py-2 pr-3">Doublons</th>
                  <th className="py-2 pr-3">Exclus</th>
                  <th className="py-2">Erreurs</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.run_id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 text-xs font-mono text-slate-600">
                      {r.started_at?.slice(0, 19).replace('T', ' ')}
                    </td>
                    <td className="py-2 pr-3 text-emerald-700 font-semibold">
                      {r.ok_count}
                    </td>
                    <td className="py-2 pr-3 text-slate-500">{r.dup_count}</td>
                    <td className="py-2 pr-3 text-amber-600">
                      {r.excluded_count}
                    </td>
                    <td className="py-2 text-xs text-red-600">
                      {(r.errors || []).length === 0 ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <span title={(r.errors || []).join(' · ')}>
                          {(r.errors || []).length} erreur(s)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
